import { ColumnPicker } from "@dashboard/components/Datagrid/ColumnPicker/ColumnPicker";
import { useColumns } from "@dashboard/components/Datagrid/ColumnPicker/useColumns";
import Datagrid from "@dashboard/components/Datagrid/Datagrid";
import {
  DatagridChangeStateContext,
  useDatagridChangeState,
} from "@dashboard/components/Datagrid/hooks/useDatagridChange";
import { TablePaginationWithContext } from "@dashboard/components/TablePagination";
import { getPrevLocationState } from "@dashboard/hooks/useBackLinkWithState";
import { ListProps, SortPage } from "@dashboard/types";
import { Item } from "@glideapps/glide-data-grid";
import { Box, useTheme } from "@saleor/macaw-ui-next";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";

import { CheckoutListUrlSortField, checkoutUrl } from "../../urls";
import { PaymentRecoveryCheckout } from "../../utils";
import {
  canBeSorted,
  createGetCellContent,
  paymentRecoveryListStaticColumnsAdapter,
} from "./datagrid";
import { messages } from "./messages";

interface PaymentRecoveryListDatagridProps extends ListProps, SortPage<CheckoutListUrlSortField> {
  checkouts: PaymentRecoveryCheckout[];
  hasRowHover?: boolean;
  /** An empty queue is good news; an empty search result is not. */
  hasSearch?: boolean;
  onRowClick?: (id: string) => void;
}

export const PaymentRecoveryListDatagrid = ({
  disabled,
  checkouts,
  sort,
  onSort,
  hasRowHover,
  hasSearch,
  onRowClick,
  settings,
  onUpdateListSettings,
}: PaymentRecoveryListDatagridProps) => {
  const location = useLocation();
  const intl = useIntl();
  const { theme } = useTheme();
  const datagridState = useDatagridChangeState();
  const handleColumnChange = useCallback(
    (picked: string[]) => {
      if (onUpdateListSettings) {
        onUpdateListSettings("columns", picked.filter(Boolean));
      }
    },
    [onUpdateListSettings],
  );
  const memoizedStaticColumns = useMemo(
    () => paymentRecoveryListStaticColumnsAdapter(intl, sort),
    [intl, sort],
  );
  const { handlers, staticColumns, visibleColumns, selectedColumns } = useColumns({
    gridName: "payment_recovery_list",
    staticColumns: memoizedStaticColumns,
    selectedColumns: settings?.columns ?? [],
    onSave: handleColumnChange,
  });
  const getCellContent = useCallback(
    createGetCellContent({ checkouts, columns: visibleColumns, intl, currentTheme: theme }),
    [checkouts, visibleColumns, intl, theme],
  );
  const handleHeaderClick = useCallback(
    (col: number) => {
      const columnName = visibleColumns[col].id;

      if (canBeSorted(columnName)) {
        onSort(columnName);
      }
    },
    [visibleColumns, onSort],
  );
  const handleRowClick = useCallback(
    ([_, row]: Item) => {
      if (!onRowClick) {
        return;
      }

      onRowClick(checkouts[row].id);
    },
    [onRowClick, checkouts],
  );
  const handleRowAnchor = useCallback(
    ([, row]: Item) => checkoutUrl(checkouts[row].id),
    [checkouts],
  );

  return (
    <DatagridChangeStateContext.Provider value={datagridState}>
      <Datagrid
        readonly
        rowMarkers="none"
        columnSelect="single"
        freezeColumns={1}
        hasRowHover={hasRowHover}
        loading={disabled}
        availableColumns={visibleColumns}
        verticalBorder={false}
        getCellContent={getCellContent}
        getCellError={() => false}
        menuItems={() => []}
        emptyText={intl.formatMessage(hasSearch ? messages.emptyTextSearch : messages.emptyText)}
        rows={checkouts?.length ?? 0}
        selectionActions={() => null}
        onColumnResize={handlers.onResize}
        onColumnMoved={handlers.onMove}
        onHeaderClicked={handleHeaderClick}
        onRowClick={handleRowClick}
        rowAnchor={handleRowAnchor}
        renderColumnPicker={() => (
          <ColumnPicker
            staticColumns={staticColumns}
            selectedColumns={selectedColumns}
            onToggle={handlers.onToggle}
          />
        )}
        navigatorOpts={{ state: getPrevLocationState(location) }}
      />

      <Box paddingX={6}>
        <TablePaginationWithContext
          component="div"
          colSpan={1}
          settings={settings}
          disabled={disabled}
          onUpdateListSettings={onUpdateListSettings}
        />
      </Box>
    </DatagridChangeStateContext.Provider>
  );
};

PaymentRecoveryListDatagrid.displayName = "PaymentRecoveryListDatagrid";
export default PaymentRecoveryListDatagrid;
